import { REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { homApi } from '~/redux/apis/hom'
import { CURRICULUM_TYPE } from '~/redux/constants/tags.const'
import { setChecklistNotification } from '~/redux/slices/checklist'
import { CountryDetailsType } from '~/types/common.types'

import { normalizeCountryCode } from '../utils/helpers'
import {
  CurriculumCertificate,
  CurriculumCertificatePatchRequestBody,
  CurriculumCertificateResponse,
  CurriculumMetadataResponse,
  GenerateCurriculumCertificateRequestBody,
  GradeSubject,
} from '../types/curriculum.types'

export const curriculumUpdateApi = homApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentGrade: builder.query<
      {
        grade: number | null
        subject: string | null
        subjectOrder: number | null
        subTopic: string | null
        subTopicOrder: number | null
      },
      void | { familyMemberId?: number }
    >({
      query: (params) => ({
        url: `/v1/curriculum/current-grade${params?.familyMemberId ? `?familyMemberId=${params.familyMemberId}` : ''}`,
        method: 'GET',
      }),
      providesTags: [CURRICULUM_TYPE.CURRICULUM_CURRENT_GRADE],
    }),
    getGrades: builder.mutation<number[], void>({
      query: () => ({
        url: '/v1/curriculum/grades',
        method: 'GET',
      }),
    }),
    setGrades: builder.mutation<
      { success: boolean },
      {
        curriculumGrade: number
        fromOnboarding?: boolean
        subject?: string | null
        subjectOrder?: number | null
        subTopic?: string | null
        subTopicOrder?: number | null
        replaceFullRow?: boolean
      }
    >({
      query: ({ curriculumGrade, fromOnboarding, subject, subjectOrder, subTopic, subTopicOrder, replaceFullRow }) => {
        const body: {
          curriculumGrade: number
          fromOnboarding?: boolean
          subject?: string | null
          subjectOrder?: number | null
          subTopic?: string | null
          subTopicOrder?: number | null
          replaceFullRow?: boolean
        } = { curriculumGrade }
        if (fromOnboarding !== undefined) body.fromOnboarding = fromOnboarding
        if (subject !== undefined) body.subject = subject
        if (subjectOrder !== undefined) body.subjectOrder = subjectOrder
        if (subTopic !== undefined) body.subTopic = subTopic
        if (subTopicOrder !== undefined) body.subTopicOrder = subTopicOrder
        if (replaceFullRow !== undefined) body.replaceFullRow = replaceFullRow
        return {
          url: '/v1/curriculum/grades',
          method: 'POST',
          body,
        }
      },
      invalidatesTags: [
        CURRICULUM_TYPE.CURRICULUM_CURRENT_GRADE,
        CURRICULUM_TYPE.CURRICULUM_USER_STATS,
        CURRICULUM_TYPE.CURRICULUM_LEVEL_PROGRESS,
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const result = await queryFulfilled
        if (result.data.success) {
          dispatch(setChecklistNotification({ rewardFeature: REWARD_FEATURE.PICK_CURRICULUM }))
        }
      },
    }),
    resetGrades: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: '/v1/curriculum/reset-grades',
        method: 'POST',
      }),
      invalidatesTags: [
        CURRICULUM_TYPE.CURRICULUM_CURRENT_GRADE,
        CURRICULUM_TYPE.CURRICULUM_USER_STATS,
        CURRICULUM_TYPE.CURRICULUM_LEVEL_PROGRESS,
      ],
    }),
    deleteGrades: builder.mutation<{ success: boolean }, void>({
      query: (curriculumGrade) => ({
        url: '/v1/curriculum/grades',
        method: 'DELETE',
        body: curriculumGrade,
      }),
      invalidatesTags: [CURRICULUM_TYPE.CURRICULUM_CURRENT_GRADE],
    }),
    getCurriculumCountries: builder.query<CountryDetailsType[], void>({
      query: () => ({
        url: '/v1/curriculum/countries',
        method: 'GET',
      }),

      transformResponse(response: { curriculumCountries: CountryDetailsType[] }) {
        return response.curriculumCountries
      },
    }),
    getCurriculumCertificate: builder.query<
      CurriculumCertificateResponse,
      { gradeLevel: number; subjectOrder: number; countryCode: string }
    >({
      query: (params) => ({
        url: '/v1/curriculum/certificate',
        method: 'GET',
        params: normalizeCountryCode(params),
      }),
      // Both certificate mutations below already invalidate this tag, but only the plural
      // `getCurriculumCertificates` provided it — so claiming a certificate refreshed the list and
      // left this one holding its pre-claim answer until the page was reloaded. That is what showed
      // a freshly earned certificate with no date on it: the card had the completion, because it
      // counts sub-topics itself, and not the certificate the date comes from.
      providesTags: [CURRICULUM_TYPE.CURRICULUM_CERTIFICATE],
    }),
    updateCurriculumCertificate: builder.mutation<{ success: boolean }, CurriculumCertificatePatchRequestBody>({
      query: (data) => ({
        url: '/v1/curriculum/certificate',
        method: 'PATCH',
        body: normalizeCountryCode(data),
      }),
      invalidatesTags: [CURRICULUM_TYPE.CURRICULUM_CERTIFICATE],
    }),
    generateCurriculumCertificate: builder.mutation<{ success: boolean }, GenerateCurriculumCertificateRequestBody>({
      query: (data) => ({
        url: '/v1/curriculum/certificate',
        method: 'POST',
        body: normalizeCountryCode(data),
      }),
      invalidatesTags: [CURRICULUM_TYPE.CURRICULUM_CERTIFICATE],
    }),
    getCurriculumCertificates: builder.query<CurriculumCertificate[], void>({
      query: () => ({
        url: '/v1/curriculum/certificates',
        method: 'GET',
      }),
      providesTags: [CURRICULUM_TYPE.CURRICULUM_CERTIFICATE],
    }),
    getCurriculumMappingQuizMetadata: builder.query<
      CurriculumMetadataResponse,
      {
        countryCode: string
        gradeLevel: string
      }
    >({
      query: (params) => ({
        url: '/v1/curriculum/mapping-quiz-metadata',
        method: 'GET',
        params,
      }),
    }),
    getCurriculumContent: builder.query<GradeSubject[], { countryCode: string; gradeId: string }>({
      query: (params) => ({
        url: '/v1/curriculum/content',
        method: 'GET',
        params,
      }),
    }),

    getCurriculumData: builder.query<
      { grade: number | null; subject: string | null; subjectOrder: number | null; topicId: string | null },
      { countryCode: string; grade: number | undefined }
    >({
      query: (params) => ({
        url: '/v1/curriculum/curriculum-data',
        method: 'GET',
        params,
      }),
      providesTags: [CURRICULUM_TYPE.CURRICULUM_USER_STATS, CURRICULUM_TYPE.CURRICULUM_LEVEL_PROGRESS],
    }),
  }),
})
export const {
  useGetCurrentGradeQuery,
  useGetGradesMutation,
  useSetGradesMutation,
  useResetGradesMutation,
  useDeleteGradesMutation,
  useGetCurriculumCountriesQuery,
  useGetCurriculumCertificateQuery,
  useGenerateCurriculumCertificateMutation,
  useUpdateCurriculumCertificateMutation,
  useGetCurriculumCertificatesQuery,
  useGetCurriculumMappingQuizMetadataQuery,
  useGetCurriculumContentQuery,
  useGetCurriculumDataQuery,
} = curriculumUpdateApi

export const { getCurrentGrade, getCurriculumData, getCurriculumCountries } = curriculumUpdateApi.endpoints
export const { useQueryState: useGetCurrentGradeQueryState } = getCurrentGrade
